if (!customElements.get("el-product-form")) {
  customElements.define(
    "el-product-form",
    class ElProductForm extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === "true") return;
        this.dataset.ready = "true";

        this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
        this.productData = this.readProductData();

        this.variantInput = this.querySelector("[data-el-variant-id]");

        // Legacy bundle-option support.
        this.bundleOptions = Array.from(
          this.querySelectorAll("[data-bundle-option]"),
        );

        // Subscription and one-time purchase selectors.
        this.purchaseOptions = Array.from(
          this.querySelectorAll("[data-purchase-option]"),
        );
        this.purchaseTriggers = Array.from(
          this.querySelectorAll("[data-purchase-trigger]"),
        );
        this.subscriptionVariantOptions = Array.from(
          this.querySelectorAll("[data-subscription-variant-option]"),
        );

        this.atcButton = this.querySelector("[data-el-atc-button]");
        this.atcText = this.querySelector("[data-el-atc-text]");
        this.atcPrice = this.querySelector("[data-el-atc-price]");
        this.atcBundleLine = this.querySelector("[data-el-atc-bundle-line]");
        this.atcBundleTitle = this.querySelector("[data-el-atc-bundle-title]");
        this.atcInlineMeta = this.querySelector("[data-el-atc-inline-meta]");
        this.showBundleTitleInButton =
          this.atcButton?.dataset.showBundleTitle === "true";

        this.onBundleClick = this.onBundleClick.bind(this);
        this.onPurchaseTriggerClick = this.onPurchaseTriggerClick.bind(this);
        this.onSubscriptionVariantClick =
          this.onSubscriptionVariantClick.bind(this);
        this.onAddToCart = this.onAddToCart.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onThemeVariantChange = this.onThemeVariantChange.bind(this);

        this.bundleOptions.forEach((option) => {
          option.addEventListener("click", this.onBundleClick);
        });

        this.purchaseTriggers.forEach((trigger) => {
          trigger.addEventListener("click", this.onPurchaseTriggerClick);
        });

        this.subscriptionVariantOptions.forEach((option) => {
          option.addEventListener("click", this.onSubscriptionVariantClick);
        });

        if (this.atcButton) {
          this.atcButton.addEventListener("click", this.onAddToCart);
        }

        document.addEventListener("change", this.onVariantChange);
        document.addEventListener("variant:change", this.onThemeVariantChange);
        document.addEventListener(
          "product:variant-change",
          this.onThemeVariantChange,
        );

        const initialVariantId =
          this.variantInput?.value || this.findExternalVariantInput()?.value;

        this.setVariant(initialVariantId, false);

        if (this.purchaseOptions.length) {
          this.ensureDefaultPurchase();
        } else {
          this.ensureDefaultBundle();
        }

        this.updatePrices();
      }

      disconnectedCallback() {
        this.bundleOptions.forEach((option) => {
          option.removeEventListener("click", this.onBundleClick);
        });

        this.purchaseTriggers.forEach((trigger) => {
          trigger.removeEventListener("click", this.onPurchaseTriggerClick);
        });

        this.subscriptionVariantOptions.forEach((option) => {
          option.removeEventListener("click", this.onSubscriptionVariantClick);
        });

        if (this.atcButton) {
          this.atcButton.removeEventListener("click", this.onAddToCart);
        }

        document.removeEventListener("change", this.onVariantChange);
        document.removeEventListener(
          "variant:change",
          this.onThemeVariantChange,
        );
        document.removeEventListener(
          "product:variant-change",
          this.onThemeVariantChange,
        );
      }

      readProductData() {
        const jsonEl = this.querySelector("[data-el-product-json]");

        if (!jsonEl) return null;

        try {
          return JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.warn("Product JSON could not be read:", error);
          return null;
        }
      }

      findExternalVariantInput() {
        const scope = this.closest(".shopify-section") || document;

        return scope.querySelector(
          'form[action*="/cart/add"] select[name="id"], form[action*="/cart/add"] input[name="id"]',
        );
      }

      getVariantById(id) {
        if (!this.productData || !Array.isArray(this.productData.variants)) {
          return null;
        }

        return (
          this.productData.variants.find(
            (variant) => String(variant.id) === String(id),
          ) || null
        );
      }

      setVariant(id, shouldUpdate = true) {
        if (!id) return;

        const variant = this.getVariantById(id);
        if (!variant) return;

        this.currentVariant = variant;

        if (this.variantInput) {
          this.variantInput.value = variant.id;
        }

        const selectedPurchase = this.getSelectedPurchaseOption();

        const matchingVariantBundleOption = this.purchaseOptions.find(
          (option) =>
            option.dataset.variantBundleOption === "true" &&
            String(option.dataset.variantId) === String(variant.id),
        );

        if (
          matchingVariantBundleOption &&
          matchingVariantBundleOption.dataset.available !== "false"
        ) {
          this.selectPurchaseOption(matchingVariantBundleOption, shouldUpdate);
          return;
        }

        if (selectedPurchase?.dataset.purchaseType === "subscription") {
          const matchingSubscriptionOption =
            this.subscriptionVariantOptions.find(
              (option) =>
                String(option.dataset.variantId) === String(variant.id) &&
                option.closest("[data-purchase-option]") === selectedPurchase,
            );

          if (
            matchingSubscriptionOption &&
            matchingSubscriptionOption.dataset.available !== "false"
          ) {
            this.selectSubscriptionVariant(
              matchingSubscriptionOption,
              shouldUpdate,
            );
            return;
          }
        }

        if (shouldUpdate) {
          this.updatePrices();
        }
      }

      onVariantChange(event) {
        const target = event.target;

        if (!target.matches('select[name="id"], input[name="id"]')) return;
        if (target === this.variantInput) return;

        this.setVariant(target.value);
      }

      onThemeVariantChange(event) {
        const detail = event.detail || {};
        const variant =
          detail.variant ||
          detail.productVariant ||
          detail.currentVariant ||
          null;

        if (variant && variant.id) {
          this.setVariant(variant.id);
        }
      }

      ensureDefaultPurchase() {
        if (!this.purchaseOptions.length) return;

        const selectedPurchase =
          this.purchaseOptions.find(
            (option) => option.dataset.defaultSelected === "true",
          ) ||
          this.purchaseOptions.find((option) =>
            option.classList.contains("is-active"),
          ) ||
          this.purchaseOptions[0];

        this.selectPurchaseOption(selectedPurchase, false);

        if (selectedPurchase.dataset.purchaseType === "subscription") {
          const subscriptionOptions =
            this.getSubscriptionOptions(selectedPurchase);

          const selectedVariant =
            subscriptionOptions.find(
              (option) => option.dataset.defaultSelected === "true",
            ) ||
            subscriptionOptions.find((option) =>
              option.classList.contains("is-active"),
            ) ||
            subscriptionOptions.find(
              (option) => option.dataset.available !== "false",
            );

          if (selectedVariant) {
            this.selectSubscriptionVariant(selectedVariant, false);
          }
        }

        this.updatePrices();
      }
      onPurchaseTriggerClick(event) {
        if (event.target.closest("[data-subscription-variant-option]")) {
          return;
        }

        const purchaseOption = event.currentTarget.closest(
          "[data-purchase-option]",
        );

        this.selectPurchaseOption(purchaseOption);
      }
      selectPurchaseOption(selectedOption, shouldUpdate = true) {
        if (!selectedOption || selectedOption.disabled) return;

        this.purchaseOptions.forEach((option) => {
          const isActive = option === selectedOption;

          option.classList.toggle("is-active", isActive);
          option.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

        // Purchase blocks and legacy bundle blocks are mutually exclusive.
        this.bundleOptions.forEach((option) => {
          option.classList.remove("is-active");
          option.setAttribute("aria-pressed", "false");
        });

        const selectedVariant = this.getVariantById(
          selectedOption.dataset.variantId,
        );

        if (selectedVariant) {
          this.currentVariant = selectedVariant;
        }

        if (selectedOption.dataset.purchaseType === "subscription") {
          let selectedVariant =
            this.getSelectedSubscriptionOption(selectedOption);

          if (!selectedVariant) {
            selectedVariant = this.getSubscriptionOptions(selectedOption).find(
              (option) => option.dataset.available !== "false",
            );
          }

          if (selectedVariant) {
            this.selectSubscriptionVariant(selectedVariant, false);
          } else {
            /*
             * A product with only the default variant has no visible
             * subscription variant buttons. Its purchase data is stored
             * directly on the subscription card.
             */
            this.syncVariantInput(selectedOption.dataset.variantId);
          }
        } else {
          this.syncVariantInput(selectedOption.dataset.variantId);
        }

        if (shouldUpdate) {
          this.updatePrices();
        }
      }

      onSubscriptionVariantClick(event) {
        event.stopPropagation();
        this.selectSubscriptionVariant(event.currentTarget);
      }

      selectSubscriptionVariant(selectedOption, shouldUpdate = true) {
        if (
          !selectedOption ||
          selectedOption.disabled ||
          selectedOption.dataset.available === "false"
        ) {
          return;
        }

        const subscriptionCard = selectedOption.closest(
          '[data-purchase-option][data-purchase-type="subscription"]',
        );

        if (!subscriptionCard) return;

        if (!subscriptionCard.classList.contains("is-active")) {
          this.selectPurchaseOption(subscriptionCard, false);
        }

        this.getSubscriptionOptions(subscriptionCard).forEach((option) => {
          const isActive = option === selectedOption;

          option.classList.toggle("is-active", isActive);
          option.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

        const variant = this.getVariantById(selectedOption.dataset.variantId);

        if (variant) {
          this.currentVariant = variant;
        }

        this.syncVariantInput(selectedOption.dataset.variantId);

        if (shouldUpdate) {
          this.updatePrices();
        }
      }

      getSubscriptionOptions(subscriptionCard) {
        if (!subscriptionCard) return [];

        return Array.from(
          subscriptionCard.querySelectorAll(
            "[data-subscription-variant-option]",
          ),
        );
      }

      getSelectedSubscriptionOption(subscriptionCard) {
        return (
          this.getSubscriptionOptions(subscriptionCard).find((option) =>
            option.classList.contains("is-active"),
          ) || null
        );
      }

      getSelectedPurchaseOption() {
        return (
          this.purchaseOptions.find((option) =>
            option.classList.contains("is-active"),
          ) || null
        );
      }

      getSelectedPurchaseData() {
        const purchaseOption = this.getSelectedPurchaseOption();

        if (!purchaseOption) return null;

        const purchaseType = purchaseOption.dataset.purchaseType;

        if (purchaseType === "subscription") {
          const selectedVariant =
            this.getSelectedSubscriptionOption(purchaseOption);

          /*
           * Multi-variant products read from the selected variant button.
           * Single-variant products read directly from the parent card.
           */
          const purchaseSource = selectedVariant || purchaseOption;
          const sellingPlanId = purchaseSource.dataset.sellingPlanId || "";

          return {
            type: "subscription",
            variantId: Number(purchaseSource.dataset.variantId || 0),
            sellingPlanId: Number(sellingPlanId || 0),
            price: Number(purchaseSource.dataset.price || 0),
            comparePrice: Number(purchaseSource.dataset.comparePrice || 0),
            title:
              purchaseSource.dataset.purchaseTitle ||
              purchaseOption.dataset.purchaseTitle ||
              "",
            available:
              purchaseSource.dataset.available !== "false" &&
              Boolean(sellingPlanId),
            quantity: 1,
          };
        }

        return {
          type: "one_time",
          variantId: Number(purchaseOption.dataset.variantId || 0),
          sellingPlanId: 0,
          price: Number(purchaseOption.dataset.price || 0),
          comparePrice: Number(purchaseOption.dataset.comparePrice || 0),
          title: purchaseOption.dataset.purchaseTitle || "One time",
          available: purchaseOption.dataset.available !== "false",
          quantity: 1,
        };
      }

      syncVariantInput(variantId) {
        if (this.variantInput && variantId) {
          this.variantInput.value = variantId;
        }
      }

      ensureDefaultBundle() {
        if (!this.bundleOptions.length) return;

        const selected =
          this.bundleOptions.find(
            (option) => option.dataset.defaultSelected === "true",
          ) ||
          this.bundleOptions.find((option) =>
            option.classList.contains("is-active"),
          ) ||
          this.bundleOptions[0];

        this.selectBundle(selected, false);
      }

      onBundleClick(event) {
        this.selectBundle(event.currentTarget);
      }

      selectBundle(selectedOption, shouldUpdate = true) {
        if (!selectedOption) return;

        this.bundleOptions.forEach((option) => {
          const isActive = option === selectedOption;

          option.classList.toggle("is-active", isActive);
          option.setAttribute("aria-pressed", isActive ? "true" : "false");
        });

        this.purchaseOptions.forEach((option) => {
          option.classList.remove("is-active");
          option.setAttribute("aria-pressed", "false");
        });

        if (shouldUpdate) {
          this.updatePrices();
        }
      }

      getSelectedBundle() {
        return (
          this.bundleOptions.find((option) =>
            option.classList.contains("is-active"),
          ) || null
        );
      }

      getBundlePrice(option) {
        if (!this.currentVariant || !option) return 0;

        const price = Number(this.currentVariant.price || 0);
        const qty = Number(option.dataset.quantity || 1);
        const discountPercent = Number(option.dataset.discountPercent || 0);
        const multiplier = (100 - discountPercent) / 100;

        return Math.round(price * qty * multiplier);
      }

      getBundleComparePrice(option) {
        if (!this.currentVariant || !option) return 0;

        const price = Number(this.currentVariant.price || 0);
        const compareBase = Number(
          this.currentVariant.compare_at_price || price,
        );
        const qty = Number(option.dataset.quantity || 1);
        const compareQty = Number(option.dataset.compareQuantity || 0);

        if (compareQty > 0) {
          return Math.round(compareBase * compareQty);
        }

        return Math.round(compareBase * qty);
      }

      getCurrentAtcPrice() {
        const selectedPurchase = this.getSelectedPurchaseOption();
        const purchaseData = this.getSelectedPurchaseData();

        if (selectedPurchase) {
          return purchaseData ? purchaseData.price : 0;
        }

        if (!this.currentVariant) return 0;

        const selectedBundle = this.getSelectedBundle();

        if (selectedBundle) {
          return this.getBundlePrice(selectedBundle);
        }

        return Number(this.currentVariant.price || 0);
      }

      getCurrentComparePrice() {
        const selectedPurchase = this.getSelectedPurchaseOption();
        const purchaseData = this.getSelectedPurchaseData();

        if (selectedPurchase) {
          return purchaseData ? purchaseData.comparePrice : 0;
        }

        const selectedBundle = this.getSelectedBundle();

        if (selectedBundle) {
          return this.getBundleComparePrice(selectedBundle);
        }

        return Number(this.currentVariant?.compare_at_price || 0);
      }

      getCurrentSelectionTitle() {
        const selectedPurchase = this.getSelectedPurchaseOption();
        const purchaseData = this.getSelectedPurchaseData();

        if (selectedPurchase) {
          return purchaseData ? purchaseData.title : "";
        }

        return this.getSelectedBundle()?.dataset.bundleTitle || "";
      }

      getCurrentSelectionAvailable() {
        const selectedPurchase = this.getSelectedPurchaseOption();
        const purchaseData = this.getSelectedPurchaseData();

        if (selectedPurchase) {
          return Boolean(purchaseData?.available);
        }

        return Boolean(this.currentVariant?.available);
      }

      getCurrentVariantId() {
        const selectedPurchase = this.getSelectedPurchaseOption();
        const purchaseData = this.getSelectedPurchaseData();

        if (selectedPurchase) {
          return purchaseData ? purchaseData.variantId : 0;
        }

        return Number(this.currentVariant?.id || 0);
      }

      updatePrices() {
        if (!this.currentVariant && !this.getSelectedPurchaseData()) return;

        this.bundleOptions.forEach((option) => {
          this.updateBundleOptionPrice(option);
        });

        this.updateSubscriptionCardPrice();

        if (this.atcPrice) {
          this.atcPrice.textContent = this.formatMoney(
            this.getCurrentAtcPrice(),
          );
        }

        this.updateAtcButtonContent();
        this.dispatchStickyCtaUpdate();
      }

      updateSubscriptionCardPrice() {
        this.purchaseOptions
          .filter((option) => option.dataset.purchaseType === "subscription")
          .forEach((subscriptionCard) => {
            const selectedVariant =
              this.getSelectedSubscriptionOption(subscriptionCard);

            const purchaseSource = selectedVariant || subscriptionCard;

            const price = Number(purchaseSource.dataset.price || 0);
            const comparePrice = Number(
              purchaseSource.dataset.comparePrice || 0,
            );

            const priceTarget = subscriptionCard.querySelector(
              "[data-subscription-selected-price]",
            );

            const compareTarget = subscriptionCard.querySelector(
              "[data-subscription-selected-compare]",
            );

            if (priceTarget) {
              priceTarget.textContent = this.formatMoney(price);
            }

            if (compareTarget) {
              compareTarget.textContent = this.formatMoney(comparePrice);
              compareTarget.classList.toggle(
                "is-hidden",
                comparePrice <= price,
              );
            }
          });
      }

      updateBundleOptionPrice(option) {
        if (!option) return;

        const price = this.getBundlePrice(option);
        const comparePrice = this.getBundleComparePrice(option);
        const displayMode = option.dataset.priceDisplayMode || "total";
        const perProductQuantity = Math.max(
          1,
          Number(
            option.dataset.perProductQuantity || option.dataset.quantity || 1,
          ),
        );
        const perProductLabel = option.dataset.perProductLabel || "";

        const priceEl = option.querySelector("[data-bundle-price]");
        const compareEl = option.querySelector("[data-bundle-compare]");

        if (priceEl) {
          if (displayMode === "per_product") {
            const perProductPrice = Math.round(price / perProductQuantity);
            const labelHtml = perProductLabel
              ? ` <span class="el-bundle-option__price-unit">${this.escapeHtml(
                  perProductLabel,
                )}</span>`
              : "";

            priceEl.innerHTML = `${this.formatMoney(
              perProductPrice,
            )}${labelHtml}`;
          } else {
            priceEl.textContent = this.formatMoney(price);
          }
        }

        if (compareEl) {
          compareEl.textContent = this.formatMoney(comparePrice);
          compareEl.classList.toggle(
            "is-hidden",
            displayMode === "per_product" || comparePrice <= price,
          );
        }
      }

      updateAtcButtonContent() {
        if (!this.atcButton || !this.atcText) return;

        const isAvailable = this.getCurrentSelectionAvailable();

        if (isAvailable) {
          this.atcButton.disabled = false;
          this.atcText.textContent =
            this.atcButton.dataset.text || "ADD TO CART";

          if (this.showBundleTitleInButton && this.atcBundleLine) {
            const selectionTitle = this.getCurrentSelectionTitle();

            this.atcBundleLine.hidden = false;

            if (this.atcBundleTitle) {
              this.atcBundleTitle.textContent = selectionTitle;
            }
          }
        } else {
          this.atcButton.disabled = true;
          this.atcText.textContent = "SOLD OUT";

          if (this.atcBundleLine) {
            this.atcBundleLine.hidden = true;
          }
        }
      }

      dispatchStickyCtaUpdate() {
        const price = this.getCurrentAtcPrice();
        const comparePrice = this.getCurrentComparePrice();
        const showComparePrice = comparePrice > price;
        const purchaseData = this.getSelectedPurchaseData();
        const selectedBundle = this.getSelectedBundle();

        this.dispatchEvent(
          new CustomEvent("el-product-form:update", {
            bubbles: true,
            detail: {
              price,
              priceHtml: this.formatMoney(price),
              comparePrice,
              comparePriceHtml: showComparePrice
                ? this.formatMoney(comparePrice)
                : "",
              showComparePrice,
              available: this.getCurrentSelectionAvailable(),
              variantId: this.getCurrentVariantId(),
              sellingPlanId: purchaseData?.sellingPlanId || 0,
              purchaseType: purchaseData?.type || "",
              bundleTitle: this.getCurrentSelectionTitle(),
              quantity: purchaseData
                ? purchaseData.quantity
                : selectedBundle
                  ? Number(selectedBundle.dataset.quantity || 1)
                  : 1,
            },
          }),
        );
      }

      escapeHtml(value) {
        return String(value || "")
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#039;");
      }

      formatMoney(cents) {
        const numericCents = Number(cents || 0);

        if (
          window.Shopify &&
          typeof window.Shopify.formatMoney === "function"
        ) {
          return window.Shopify.formatMoney(numericCents, this.moneyFormat);
        }

        return `$${(numericCents / 100).toFixed(2)}`;
      }

      async onAddToCart() {
        if (!this.atcButton || !this.getCurrentSelectionAvailable()) {
          return;
        }

        const purchaseData = this.getSelectedPurchaseData();
        const selectedBundle = this.getSelectedBundle();
        const items = [];

        if (purchaseData) {
          const item = {
            id: purchaseData.variantId,
            quantity: 1,
          };

          if (
            purchaseData.type === "subscription" &&
            purchaseData.sellingPlanId
          ) {
            item.selling_plan = purchaseData.sellingPlanId;
          }

          items.push(item);
        } else {
          const quantity = selectedBundle
            ? Number(selectedBundle.dataset.quantity || 1)
            : 1;

          items.push({
            id: Number(this.currentVariant.id),
            quantity,
          });

          if (selectedBundle) {
            const giftVariantIds = (selectedBundle.dataset.giftVariantIds || "")
              .split(",")
              .map((id) => id.trim())
              .filter(Boolean);

            giftVariantIds.forEach((giftId) => {
              items.push({
                id: Number(giftId),
                quantity: 1,
                properties: {
                  "_Free gift": "Yes",
                  _Bundle: selectedBundle.dataset.bundleTitle || "Bundle",
                },
              });
            });
          }
        }

        const originalText = this.atcText ? this.atcText.textContent : "";
        const root = window.Shopify?.routes?.root || "/";

        try {
          this.atcButton.classList.add("is-loading");
          this.atcButton.disabled = true;

          if (this.atcText) {
            this.atcText.textContent = "ADDING";
          }

          const cartDrawer = document.querySelector("cart-drawer");
          const sections = this.getShrineCartSections(cartDrawer);

          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify({
              items,
              sections,
              sections_url: window.location.pathname,
            }),
          });

          const parsedState = await response.json();

          if (!response.ok) {
            throw new Error(
              parsedState.description ||
                parsedState.message ||
                "Could not add item to cart.",
            );
          }

          /*
            Shrine/Dawn cart drawer expects parsedState.id.
            Shopify returns parsedState.items when items: [] is used.
          */
          if (
            !parsedState.id &&
            parsedState.items &&
            parsedState.items.length
          ) {
            parsedState.id = parsedState.items[0].id;
          }

          if (!parsedState.sections) {
            parsedState.sections = await this.fetchFreshCartSections(sections);
          }

          this.renderShrineCartDrawer(parsedState, cartDrawer);
        } catch (error) {
          console.error(error);
          alert(error.message || "Something went wrong. Please try again.");
        } finally {
          this.atcButton.classList.remove("is-loading");

          if (this.atcText) {
            this.atcText.textContent = originalText;
          }

          this.updatePrices();
        }
      }

      getShrineCartSections(cartDrawer) {
        if (
          cartDrawer &&
          typeof cartDrawer.getSectionsToRender === "function"
        ) {
          return cartDrawer.getSectionsToRender().map((section) => section.id);
        }

        return ["cart-drawer", "cart-icon-bubble"];
      }

      async fetchFreshCartSections(sections) {
        const root = window.Shopify?.routes?.root || "/";
        const sectionUrl = `${root}?sections=${sections.join(",")}`;

        const response = await fetch(sectionUrl, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        return await response.json();
      }

      renderShrineCartDrawer(parsedState, cartDrawer) {
        cartDrawer = cartDrawer || document.querySelector("cart-drawer");

        if (
          cartDrawer &&
          typeof cartDrawer.renderContents === "function" &&
          parsedState.sections
        ) {
          cartDrawer.renderContents(parsedState);

          setTimeout(() => {
            this.cleanShrineEmptyCartState();
          }, 50);

          return;
        }

        this.manuallyRenderCartSections(parsedState.sections);
        this.cleanShrineEmptyCartState();
        this.forceOpenShrineCartDrawer();
      }

      manuallyRenderCartSections(sections) {
        if (!sections) return;

        Object.keys(sections).forEach((sectionId) => {
          const html = sections[sectionId];
          if (!html) return;

          const parsedHTML = new DOMParser().parseFromString(html, "text/html");

          if (sectionId === "cart-drawer") {
            const newCartDrawer = parsedHTML.querySelector("#CartDrawer");
            const currentCartDrawer = document.querySelector("#CartDrawer");

            if (newCartDrawer && currentCartDrawer) {
              currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
            }

            return;
          }

          if (sectionId === "cart-icon-bubble") {
            const newBubble =
              parsedHTML.querySelector("#cart-icon-bubble") ||
              parsedHTML.querySelector("[id*='cart-icon-bubble']");

            const currentBubble =
              document.querySelector("#cart-icon-bubble") ||
              document.querySelector("[id*='cart-icon-bubble']");

            if (newBubble && currentBubble) {
              currentBubble.innerHTML = newBubble.innerHTML;
            }
          }
        });
      }

      cleanShrineEmptyCartState() {
        const cartDrawer = document.querySelector("cart-drawer");
        const cartDrawerItems = document.querySelector("cart-drawer-items");
        const drawerInner = document.querySelector(".drawer__inner");
        const checkoutButton = document.querySelector("#CartDrawer-Checkout");

        if (cartDrawer) {
          cartDrawer.classList.remove("is-empty");
        }

        if (cartDrawerItems) {
          cartDrawerItems.classList.remove("is-empty");
        }

        if (drawerInner) {
          drawerInner.classList.remove("is-empty");
        }

        if (checkoutButton) {
          checkoutButton.disabled = false;
          checkoutButton.removeAttribute("disabled");
        }
      }

      forceOpenShrineCartDrawer() {
        const cartDrawer = document.querySelector("cart-drawer");

        if (!cartDrawer) return;

        if (typeof cartDrawer.open === "function") {
          cartDrawer.open();
          return;
        }

        cartDrawer.classList.add("animate", "active");
        cartDrawer.setAttribute("open", "");
        document.body.classList.add("overflow-hidden");
      }
    },
  );
}
